/**
 * useResources Hook
 *
 * React Query hooks for investment resource operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  resourcesService,
  CreateResourceDto,
  UpdateResourceDto,
} from '../services/resources.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { InvestmentResource } from '@medsuite/models';

export const resourceKeys = {
  all: ['resources'] as const,
  lists: () => [...resourceKeys.all, 'list'] as const,
  list: (investmentId: number) => [...resourceKeys.lists(), investmentId] as const,
};

/**
 * Fetch all resources for an investment
 */
export function useResources(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentResource[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentResource[], Error>({
    queryKey: resourceKeys.list(investmentId),
    queryFn: async () => {
      const response = await resourcesService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, filter, resources[], total}, meta: {}}
      const responseData = unwrapResponse<{ resources?: InvestmentResource[] }>(response.data);
      // The API returns {investment_id, filter, resources, total}, extract the resources array
      return responseData.resources || [];
    },
    ...options,
  });
}

/**
 * Create new resource
 */
export function useCreateResource(
  investmentId: number,
  options?: UseMutationOptions<InvestmentResource, Error, Omit<CreateResourceDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentResource, Error, Omit<CreateResourceDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await resourcesService.create(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update resource
 */
export function useUpdateResource(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentResource,
    Error,
    { resourceId: number; data: UpdateResourceDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentResource, Error, { resourceId: number; data: UpdateResourceDto }>({
    mutationFn: async ({ resourceId, data }) => {
      const response = await resourcesService.update(investmentId, resourceId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete resource
 */
export function useDeleteResource(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (resourceId) => {
      await resourcesService.delete(investmentId, resourceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}
